import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { getFacebookAccounts, getSegmentsByMail, setAudiences, updateSegment } from "../../services/globalService";
import StateContext from "../../context/StateContext";
import ToggleButton from "../utils/ToggleButton";
import ContentBox from "../../move2FComponents/ContentBox";
import SaveButton from "../../move2FComponents/SaveButton";
import SelectPixel from "./SelectPixel";
import { ReactComponent as LeftArrow } from "../../img/Left_Arrow.svg";
import { ReactComponent as FacebookIconSmall } from "../../img/FacebookIcon_small.svg";
import { ReactComponent as ErrorIcon } from "../../img/ErrorIcon.svg";
import { StyledGoBack, StyledInBoxFrame, StyledInBoxFrameHeader, StyledInBoxFrameContent, StyledSmallError } from "../style/global.style";
import { StyledInnerPage, StyledCheckboxWrapper } from "./setup.style";
import SmallLoader from "../../components/utils/SmallLoader";
import { prepWithExtras } from "../../services/dataService";
import { captureException } from "services/sentry";
import FBLoginButton from "./FacebookLoginButton";

const ConnectFacebook = () => {
	const { customer, segMap, audMap, dispatch, ACTIONS } = useContext(StateContext);
	const { code } = useParams();
	const history = useHistory();
	const [fbOn, setFbOn] = useState((segMap && segMap[code]?.install_data?.options?.fb) || false);
	const showFBAudiences = useMemo(() => segMap && segMap[code]?.install_data?.options?.fb && segMap[code]?.install_data?.options?.capi, [code, segMap]);
	const [checked, setChecked] = useState(false);
	const [accounts, setAccounts] = useState(null);
	const [accountIndex, setAccountIndex] = useState(null);
	const [pixelIndex, setPixelIndex] = useState(null);
	const [showEditMode, setShowEditMode] = useState(false);
	const [fBLoginShowSpinner, setFBLoginShowSpinner] = useState(false);
	const [showSpinner, setShowSpinner] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const getConnectedAccount = useCallback(() => {
		if (showFBAudiences && audMap && audMap[code]) {
			const account = audMap[code][0];
			const accountObj = {
				name: account?.account_name,
				adspixels: {
					data: [
						{
							name: account?.pixel_name,
							id: account?.pixel
						}
					]
				},
				account_id: account?.account.replace("act_", ""),
				id: account?.account
			};
			setAccounts([accountObj]);
			setShowEditMode(true);
		}
	}, [showFBAudiences, audMap, code]);

	useEffect(() => {
		getConnectedAccount();
	}, [getConnectedAccount]);

	const getAccounts = async (fbRes) => {
		if (fbRes.accessToken && fbRes.userID) {
			setFBLoginShowSpinner(true);
			sessionStorage.fbId = fbRes.userID;
			sessionStorage.fbToken = fbRes.accessToken;
			sessionStorage.fbExpire = fbRes.data_access_expiration_time;
			try {
				const res = await getFacebookAccounts({ id: fbRes.userID, token: fbRes.accessToken });
				if (res?.data?.data) {
					setAccounts(res.data.data);
				}
			} catch (e) {
				connectionFailed(e.data);
				captureException(e, "error", {}, { action: "getAccounts", description: "failed getting accounts from faceook" }, { scope: "connectFacebook.js" });
			} finally {
				setFBLoginShowSpinner(false);
			}
		}
	};

	const connectionFailed = (fbRes) => {
		sessionStorage.removeItem("fbId");
		sessionStorage.removeItem("fbToken");
		if (fbRes.status !== "unknown") setErrorMessage("Failed connecting to FB, Try again later");
		setTimeout(() => setErrorMessage(""), 2000);
		if (showSpinner) setShowSpinner(false);
	};

	const setFBAccount = async (seg) => {
		const data = {
			token: sessionStorage.fbToken,
			display: seg.display,
			segment: seg.code,
			account: accounts[accountIndex].id,
			account_name: accounts[accountIndex].name,
			pixel: accounts[accountIndex].adspixels.data[pixelIndex].id,
			pixel_name: accounts[accountIndex].adspixels.data[pixelIndex].name
		};
		const res = await setAudiences(data);
		return res?.data;
	};

	const onSave = async (e) => {
		e.preventDefault();
		try {
			setShowSpinner(true);
			let dataWithFB = {};
			// update the segment only if have code
			const newSeg = { ...segMap[code] };
			newSeg.install_data.options.fb = fbOn;
			if (pixelIndex !== null && !accountIndex !== null && !checked) {
				dataWithFB = await setFBAccount(newSeg);
			}
			if (checked) {
				newSeg.install_data.options.capi = false;
			}
			if (!checked && fbOn) newSeg.install_data.options.capi = true;
			// install_data.options.fb
			const res = await updateSegment({ id: code, data: { install_data: newSeg.install_data } });
			if (res?.data && dataWithFB) {
				const resWithExtras = await getSegmentsByMail({ mail: customer?.mail, extras: ["audiences"] });
				const { segMap: _segMap, audMap: _audMap, segmentsArray } = prepWithExtras({ segments: resWithExtras.data });
				const payload = { segMap: _segMap, audMap: _audMap, segments: segmentsArray };
				setErrorMessage("");
				dispatch({ type: ACTIONS.SET_STATE, payload });
				setShowSpinner(false);
				history.goBack();
			}

		} catch (e) {
			setShowSpinner(false);
			const payload = { selectedSegmentCode: null };
			setErrorMessage(e.data);
			dispatch({ type: ACTIONS.SET_STATE, payload });
			console.error("CreateSegment.js ~ saveName ~ error", e);
			captureException(e, "error", {}, { action: "onSave", description: "failed saving changes on segment install data" }, { scope: "connectFacebook.js" });
		}
	};
	// set spinner off when component unmounted
	useEffect(() => setShowSpinner(false), []);

	const renderSelectPixel = () => {
		if (showFBAudiences && showEditMode) {
			return (
				<SelectPixel
					disabled={showSpinner || fBLoginShowSpinner}
					accounts={accounts}
					isEditMode={showEditMode}
					setShowEditMode={setShowEditMode}
					getAccounts={getAccounts}
					connectionFailed={connectionFailed}
				/>
			);
		}
		return (
			<SelectPixel
				disabled={showSpinner || fBLoginShowSpinner}
				accounts={accounts}
				accountIndex={accountIndex}
				setAccountIndex={setAccountIndex}
				pixelIndex={pixelIndex}
				setPixelIndex={setPixelIndex}
			/>
		);
	};
	const renderLoginFBButton = () => {
		return <FBLoginButton onSuccess={getAccounts} onFail={connectionFailed} showSpinner={fBLoginShowSpinner || showSpinner} />;
	};

	const isDisabled = () => {
		let enabled = false;
		if (!fbOn) enabled = true; //fb is off
		// 2. if fbOn && checked
		if (fbOn && checked) enabled = true; //fb on and don’t want the advanced audiences integration
		// 3. if fbOn && !checked && !isNaN(pixelIndex) && !isNaN(accountIndex)
		if (fbOn && pixelIndex !== null && !accountIndex !== null && !checked && accounts[accountIndex]?.adspixels?.data?.length) enabled = true; //fb on and have pixel and account selected indexes
		// 4. if loading
		if (showSpinner) enabled = false;
		return !enabled;
	};

	return (
		<StyledInnerPage>
			<StyledGoBack disabled={showSpinner} as={Link} onClick={() => history.goBack()}>
				<LeftArrow />
				Go Back
			</StyledGoBack>
			<form onSubmit={onSave}>
				<ContentBox title="Reach high value users with Facebook Ads">
					<p>Fixel automagically integrates with your favorite ad platforms for creating high value audiences.</p>
					<StyledInBoxFrame>
						<StyledInBoxFrameHeader>
							<h3>
								<FacebookIconSmall />
								Facebook Ads
							</h3>
							<ToggleButton
								disabled={showSpinner}
								name="fb"
								onChange={() => {
									setFbOn(!fbOn);
								}}
								isChecked={fbOn}
							/>
						</StyledInBoxFrameHeader>
						{fbOn && !checked ? (
							<StyledInBoxFrameContent>
								<p>Our advanced integration creates a special blend of high value audiences directly in Facebook Ads for Remarketing and Lookalike purposes.</p>
								{accounts ? renderSelectPixel() : renderLoginFBButton()}
								{errorMessage ? (
									<StyledSmallError>
										<ErrorIcon />
										{errorMessage}
									</StyledSmallError>
								) : null}
							</StyledInBoxFrameContent>
						) : null}
					</StyledInBoxFrame>
					{fbOn ? (
						<StyledCheckboxWrapper>
							<input
								disabled={fBLoginShowSpinner || showSpinner}
								type="checkbox"
								name="noFb"
								id="noFb"
								checked={checked}
								onChange={() => {
									setChecked(!checked);
								}}
							/>
							<label htmlFor="noFb">I don’t want the advanced audiences integration</label>
						</StyledCheckboxWrapper>
					) : null}
					<SaveButton type="submit" label={showSpinner ? <SmallLoader inline={true} /> : "Save"} disabled={isDisabled()} alignTo="left" />
				</ContentBox>
			</form>
		</StyledInnerPage>
	);
};
export default ConnectFacebook;
